import * as i0 from '@angular/core';
import { Injectable, EventEmitter, HostBinding, Input, Output, Optional, Component, ViewChild, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { UntypedFormControl, Validators } from '@angular/forms';
import { isNaN } from 'lodash';
import { Subscription, Subject, debounceTime, map, distinctUntilChanged, BehaviorSubject, from, Observable, timer, catchError, throwError } from 'rxjs';
import * as i1$1 from '@yourcause/common/i18n';
import { I18nModule } from '@yourcause/common/i18n';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import { UtilsModule } from '@yourcause/common/utils';
import * as i1$2 from '@yourcause/common/icons';
import { IconsModule } from '@yourcause/common/icons';
import * as i2 from 'ngx-bootstrap/tooltip';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import * as i2$1 from '@yourcause/common/buttons';
import { ButtonModule } from '@yourcause/common/buttons';

class FormControlService {
    getFirstError(errors) {
        if (errors.required) {
            return {
                i18nKey: 'common:textThisInputIsRequired',
                defaultValue: 'This input is required'
            };
        }
        else if (errors.email) {
            return {
                i18nKey: 'common:textPleaseEnterValidEmail',
                defaultValue: 'Please enter a valid email'
            };
        }
        else if (errors.min) {
            return {
                i18nKey: 'common:textValueMustBeGreaterOrEqualTo',
                defaultValue: 'Please enter a value greater than or equal to __minimumValue__',
                context: {
                    minimumValue: '' + errors.min.min
                }
            };
        }
        else if (errors.max) {
            return {
                i18nKey: 'common:textValueMustBeLessOrEqualTo',
                defaultValue: 'Please enter a value less than or equal to __maximumValue__',
                context: {
                    maximumValue: '' + errors.max.max
                }
            };
        }
        else if (errors.maxlength) {
            return {
                i18nKey: 'common:textTooManyCharacters',
                defaultValue: 'Maximum of __number__ characters',
                context: {
                    number: '' + errors.maxlength.requiredLength
                }
            };
        }
        else if (errors.invalidDate) {
            return {
                i18nKey: 'common:textEnterValidDate',
                defaultValue: 'Please enter a valid date'
            };
        }
        else if (errors.startEndDateInvalid) {
            return {
                i18nKey: 'common:textPleaseEnterEndDateAfterStart',
                defaultValue: 'End date must be after start date'
            };
        }
        else if (errors.exactArrayLength) {
            const length = errors.exactArrayLength.length;
            return {
                i18nKey: 'common:textMustSelectAmountOfValues',
                defaultValue: length > 1 ? 'Must select __length__ values' : 'Must select 1 value',
                context: {
                    length
                }
            };
        }
        return errors[Object.keys(errors)[0]];
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormControlService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormControlService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormControlService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class FormControlComponent {
    get errors() {
        return this._errors;
    }
    set errors(errors) {
        this._errors = errors;
        this.error = this.firstError;
    }
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
    }
    set leftRounded(value) {
        this.bindLeftRounded = value;
    }
    set rightRounded(value) {
        this.bindRightRounded = value;
    }
    constructor(formGroup, formControlService) {
        this.formGroup = formGroup;
        this.formControlService = formControlService;
        this.showError = true;
        this.showClear = false;
        this.showHelp = true;
        this.debounced = false;
        this.debounceTime = 1500;
        this.helpTextClass = '';
        this.noMarginBottom = false;
        this.showQuestionMarkForTooltip = false;
        this.disabled = false;
        this.type = 'text';
        this.srOnlyLabel = false;
        this.tabIndex = 0;
        this.onChange = new EventEmitter();
        this.onFocus = new EventEmitter();
        this.onBlur = new EventEmitter();
        this.valueChange = new EventEmitter();
        this.validChange = new EventEmitter();
        this.hadError = false;
        this.touched = false;
        this._sub = new Subscription();
        this._changeSubject = new Subject();
    }
    get isDirty() {
        return this.control?.dirty || this.isDirtyOverride;
    }
    get firstError() {
        const errors = this.errors ||
            this.control?.errors ||
            (this.formGroup?.form?.errors?.[this.name] ?? null);
        if (errors) {
            return this.formControlService.getFirstError(errors);
        }
        return null;
    }
    get error() {
        if (this.isDirty) {
            return this._error || '';
        }
        return '';
    }
    set error(e) {
        this._error = e;
        this.validChange.emit(!e);
        if (e) {
            this.hadError = true;
        }
    }
    ngOnInit() {
        if (this.formControlOverride) {
            this.control = this.formControlOverride;
        }
        else if (this.formGroup?.form) {
            this.control = this.formGroup.form.controls[this.name];
            this._sub.add(this.formGroup.form.valueChanges.subscribe(() => {
                this.error = this.firstError;
            }));
        }
        if (!this.control) {
            this.name = new Date().getTime().toString() + Math.random();
            this.control = new UntypedFormControl(this.value, []);
        }
        this._value = this.control.value;
        this.error = this.firstError;
        if (this.debounced) {
            this._sub.add(this._changeSubject.pipe(debounceTime(this.debounceTime)).subscribe(newValue => {
                this._doOnChange(newValue);
            }));
        }
        const valueChangeObservable = this.control.parent ? this.control.parent.valueChanges
            .pipe(map(val => val[this.name]))
            .pipe(distinctUntilChanged()) : this.control.valueChanges;
        this._sub.add(valueChangeObservable.subscribe((newValue) => {
            if ((this._value !== newValue) &&
                !(isNaN(newValue) && isNaN(this._value))) {
                this._doOnChange(newValue, true);
            }
            this.error = this.firstError;
        }));
        this._sub.add(this.control.statusChanges.subscribe(() => {
            this.error = this.firstError;
        }));
        this._sub.add(this.onChange.subscribe(() => this.valueChange.emit(this._value)));
        this.addRequiredAsterisk = this.componentHasAsterisk();
    }
    _onFocus(_e) {
        this._onBlur(_e);
    }
    _onBlur(_e) {
        this.control.markAsTouched();
        this.onFocus.emit();
    }
    /**
     *
     * @param value New value
     * @param _fromControlChange Whether the change came internally, or from the `FormControl`
     */
    _doOnChange(value, _fromControlChange = false) {
        const valueIsDifferent = this._value !== value;
        // Mark as dirty only if the change came from the template
        this._value = value;
        if (!_fromControlChange &&
            (!this.isDirty || valueIsDifferent)) {
            this.control.markAsDirty();
            this.touched = true;
            this.control.setValue(value);
        }
        if (valueIsDifferent) {
            this.onChange.emit(value);
        }
        this.error = this.firstError;
    }
    _onChange(value, immediate = false) {
        if (!immediate && this.debounced) {
            this._changeSubject.next(value);
            this._doOnChange(value);
        }
        else {
            this._doOnChange(value);
        }
    }
    componentHasAsterisk() {
        if (this.addRequiredAsterisk === false) {
            return false;
        }
        return this.addRequiredAsterisk ||
            this.control.hasValidator(Validators.required);
    }
    ngOnDestroy() {
        this._sub.unsubscribe();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormControlComponent, deps: [{ token: i1.FormGroupDirective, optional: true }, { token: FormControlService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormControlComponent, isStandalone: false, selector: "yc-form-control", inputs: { showError: "showError", showClear: "showClear", showHelp: "showHelp", customError: "customError", debounced: "debounced", debounceTime: "debounceTime", formControlOverride: "formControlOverride", help: "help", description: "description", helpTextClass: "helpTextClass", noMarginBottom: "noMarginBottom", isDirtyOverride: "isDirtyOverride", errors: "errors", name: "name", disabledDisplayValue: "disabledDisplayValue", label: "label", tooltipText: "tooltipText", popoverTemplate: "popoverTemplate", showQuestionMarkForTooltip: "showQuestionMarkForTooltip", disabled: "disabled", type: "type", srOnlyLabel: "srOnlyLabel", leftIcon: "leftIcon", rightIcons: "rightIcons", addRequiredAsterisk: "addRequiredAsterisk", tabIndex: "tabIndex", customLabelIcon: "customLabelIcon", customLabelIconTooltip: "customLabelIconTooltip", customLabelIconClass: "customLabelIconClass", value: "value", leftRounded: "leftRounded", rightRounded: "rightRounded" }, outputs: { onChange: "onChange", onFocus: "onFocus", onBlur: "onBlur", valueChange: "valueChange", validChange: "validChange" }, host: { properties: { "class.form-control-left-rounded": "this.bindLeftRounded", "class.form-control-right-rounded": "this.bindRightRounded" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormControlComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'yc-form-control',
                    template: '<ng-content></ng-content>',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i1.FormGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: FormControlService }], propDecorators: { showError: [{
                type: Input
            }], showClear: [{
                type: Input
            }], showHelp: [{
                type: Input
            }], customError: [{
                type: Input
            }], debounced: [{
                type: Input
            }], debounceTime: [{
                type: Input
            }], formControlOverride: [{
                type: Input
            }], help: [{
                type: Input
            }], description: [{
                type: Input
            }], helpTextClass: [{
                type: Input
            }], noMarginBottom: [{
                type: Input
            }], isDirtyOverride: [{
                type: Input
            }], errors: [{
                type: Input
            }], name: [{
                type: Input
            }], disabledDisplayValue: [{
                type: Input
            }], label: [{
                type: Input
            }], tooltipText: [{
                type: Input
            }], popoverTemplate: [{
                type: Input
            }], showQuestionMarkForTooltip: [{
                type: Input
            }], disabled: [{
                type: Input
            }], type: [{
                type: Input
            }], srOnlyLabel: [{
                type: Input
            }], leftIcon: [{
                type: Input
            }], rightIcons: [{
                type: Input
            }], addRequiredAsterisk: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], customLabelIcon: [{
                type: Input
            }], customLabelIconTooltip: [{
                type: Input
            }], customLabelIconClass: [{
                type: Input
            }], value: [{
                type: Input
            }], onChange: [{
                type: Output
            }], onFocus: [{
                type: Output
            }], onBlur: [{
                type: Output
            }], valueChange: [{
                type: Output
            }], validChange: [{
                type: Output
            }], leftRounded: [{
                type: Input
            }], bindLeftRounded: [{
                type: HostBinding,
                args: ['class.form-control-left-rounded']
            }], rightRounded: [{
                type: Input
            }], bindRightRounded: [{
                type: HostBinding,
                args: ['class.form-control-right-rounded']
            }] } });

class FormErrorComponent {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormErrorComponent, isStandalone: false, selector: "yc-form-error", inputs: { error: "error" }, ngImport: i0, template: "\n<div>\n  <yc-i18n\n    *ngIf=\"error?.i18nKey\"\n    [key]=\"error?.i18nKey\"\n    [context]=\"error?.context\"\n    [defaultValue]=\"error?.defaultValue\"\n  ></yc-i18n>\n  {{ error?.errorMessage }}\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: i1$1.I18nComponent, selector: "yc-i18n", inputs: ["key", "defaultValue", "context", "transform"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormErrorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-form-error', standalone: false, template: "\n<div>\n  <yc-i18n\n    *ngIf=\"error?.i18nKey\"\n    [key]=\"error?.i18nKey\"\n    [context]=\"error?.context\"\n    [defaultValue]=\"error?.defaultValue\"\n  ></yc-i18n>\n  {{ error?.errorMessage }}\n</div>\n" }]
        }], propDecorators: { error: [{
                type: Input
            }] } });

class FormHelpComponent {
    constructor() {
        this.helpTextClass = '';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormHelpComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormHelpComponent, isStandalone: false, selector: "yc-form-help", inputs: { helpText: "helpText", helpTextClass: "helpTextClass" }, ngImport: i0, template: "<small *ngIf=\"helpText\" \n  [ngClass]=\"helpTextClass\"\n  [innerHTML]=\"helpText\">\n</small>\n", styles: [""], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormHelpComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-form-help', standalone: false, template: "<small *ngIf=\"helpText\" \n  [ngClass]=\"helpTextClass\"\n  [innerHTML]=\"helpText\">\n</small>\n" }]
        }], propDecorators: { helpText: [{
                type: Input
            }], helpTextClass: [{
                type: Input
            }] } });

class FormHelpOrErrorComponent {
    constructor() {
        this.showError = true;
        this.showHelp = true;
        this.helpTextClass = '';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormHelpOrErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormHelpOrErrorComponent, isStandalone: false, selector: "yc-form-help-or-error", inputs: { error: "error", customError: "customError", showError: "showError", showHelp: "showHelp", help: "help", helpTextClass: "helpTextClass" }, ngImport: i0, template: "<div *ngIf=\"(!!(error || customError) && showError) || (help && showHelp)\"\n  class=\"form-control-feedback\">\n  <yc-form-error *ngIf=\"(showError && (!!error || customError))\"\n    [error]=\"error || customError\">\n  </yc-form-error>\n\n  <yc-form-help *ngIf=\"showHelp && help\"\n    [helpText]=\"help\"\n    [helpTextClass]=\"helpTextClass\">\n  </yc-form-help>\n</div>", styles: [""], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: FormErrorComponent, selector: "yc-form-error", inputs: ["error"] }, { kind: "component", type: FormHelpComponent, selector: "yc-form-help", inputs: ["helpText", "helpTextClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormHelpOrErrorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-form-help-or-error', standalone: false, template: "<div *ngIf=\"(!!(error || customError) && showError) || (help && showHelp)\"\n  class=\"form-control-feedback\">\n  <yc-form-error *ngIf=\"(showError && (!!error || customError))\"\n    [error]=\"error || customError\">\n  </yc-form-error>\n\n  <yc-form-help *ngIf=\"showHelp && help\"\n    [helpText]=\"help\"\n    [helpTextClass]=\"helpTextClass\">\n  </yc-form-help>\n</div>" }]
        }], propDecorators: { error: [{
                type: Input
            }], customError: [{
                type: Input
            }], showError: [{
                type: Input
            }], showHelp: [{
                type: Input
            }], help: [{
                type: Input
            }], helpTextClass: [{
                type: Input
            }] } });

class FormLabelComponent {
    constructor() {
        this.srOnly = false;
        this.showQuestionMarkForTooltip = false;
        this.asteriskForRequired = false;
        this.forRadioButtons = false;
        this.forFileUpload = false;
    }
    ngOnInit() {
        if (this.triggerUpload) {
            this.triggerUpload.registerAction(() => {
                this.fileSelect.nativeElement.click();
            });
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormLabelComponent, isStandalone: false, selector: "yc-form-label", inputs: { name: "name", label: "label", srOnly: "srOnly", description: "description", tabIndex: "tabIndex", cursorStyle: "cursorStyle", tooltipText: "tooltipText", popoverTemplate: "popoverTemplate", showQuestionMarkForTooltip: "showQuestionMarkForTooltip", asteriskForRequired: "asteriskForRequired", forRadioButtons: "forRadioButtons", customIcon: "customIcon", customIconTooltip: "customIconTooltip", customIconClass: "customIconClass", forFileUpload: "forFileUpload", triggerUpload: "triggerUpload" }, viewQueries: [{ propertyName: "fileSelect", first: true, predicate: ["fileSelect"], descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"label\">\n  <label *ngIf=\"forFileUpload\"\n    #fileSelect\n    class=\"col-form-label\"\n    [class.radio-label]=\"forRadioButtons\"\n    [for]=\"name\"\n    [tabIndex]=\"tabIndex\"\n    role=\"button\"\n    container=\"body\"\n    [ngStyle]=\"!!cursorStyle ? {\n      cursor: cursorStyle\n    } : null\"\n    [class.sr-only]=\"srOnly\">\n    <ng-template *ngTemplateOutlet=\"innerTooltip\"></ng-template>\n  </label>\n\n  <label *ngIf=\"!forFileUpload\"\n    class=\"col-form-label\"\n    [class.radio-label]=\"forRadioButtons\"\n    [class.radio-label-no-margin]=\"!!description\"\n    [for]=\"name\"\n    container=\"body\"\n    [ngStyle]=\"!!cursorStyle ? {\n      cursor: cursorStyle\n    } : null\"\n    [class.sr-only]=\"srOnly\">\n    <ng-template *ngTemplateOutlet=\"innerTooltip\"></ng-template>\n  </label>\n\n  <div *ngIf=\"description\"\n    [class.sr-only]=\"srOnly\"\n    class=\"label-desc\"\n    [innerHTML]=\"description\">\n  </div>\n</ng-container>\n\n\n<ng-template #innerTooltip>\n  <span\n    [innerHTML]=\"label\"\n    class=\"form-label\"\n    [class.asterisk-wrapper]=\"asteriskForRequired\">\n  </span>\n  <yc-fa *ngIf=\"(tooltipText || popoverTemplate)\"\n    icon=\"question-circle\"\n    [tooltip]=\"tooltipText\"\n    [popoverTemplate]=\"popoverTemplate\"\n    [adaptivePosition]=\"false\"\n    conatiner=\"body\"\n    [class.cursor-p]=\"!!popoverTemplate\"\n    class=\"ms-2 text-link\">\n  </yc-fa>\n\n  <yc-fa *ngIf=\"customIcon\"\n    [icon]=\"customIcon\"\n    [tooltip]=\"customIconTooltip\"\n    [adaptivePosition]=\"false\"\n    conatiner=\"body\"\n    class=\"ms-3\"\n    [ngClass]=\"customIconClass\">\n  </yc-fa>\n\n  <ng-content></ng-content>\n</ng-template>", styles: [".radio-label{font-size:1rem;font-weight:500;margin-bottom:1rem;align-items:center}.radio-label.radio-label-no-margin{margin-bottom:0}.form-label{position:relative}.form-label.asterisk-wrapper:after{position:absolute;color:#eb0000;content:\" *\"}.label-desc{font-size:12px;margin-bottom:1rem}\n"], dependencies: [{ kind: "component", type: i1$2.FontAwesomeComponent, selector: "yc-fa", inputs: ["color", "icon", "tooltipText", "popoverTemplate", "light", "regular", "solid"], outputs: ["onClick"] }, { kind: "directive", type: i2.TooltipDirective, selector: "[tooltip], [tooltipHtml]", inputs: ["adaptivePosition", "tooltip", "placement", "triggers", "container", "containerClass", "boundariesElement", "isOpen", "isDisabled", "delay", "tooltipHtml", "tooltipPlacement", "tooltipIsOpen", "tooltipEnable", "tooltipAppendToBody", "tooltipAnimation", "tooltipClass", "tooltipContext", "tooltipPopupDelay", "tooltipFadeDuration", "tooltipTrigger"], outputs: ["tooltipChange", "onShown", "onHidden", "tooltipStateChanged"], exportAs: ["bs-tooltip"] }, { kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i3.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-form-label', standalone: false, template: "<ng-container *ngIf=\"label\">\n  <label *ngIf=\"forFileUpload\"\n    #fileSelect\n    class=\"col-form-label\"\n    [class.radio-label]=\"forRadioButtons\"\n    [for]=\"name\"\n    [tabIndex]=\"tabIndex\"\n    role=\"button\"\n    container=\"body\"\n    [ngStyle]=\"!!cursorStyle ? {\n      cursor: cursorStyle\n    } : null\"\n    [class.sr-only]=\"srOnly\">\n    <ng-template *ngTemplateOutlet=\"innerTooltip\"></ng-template>\n  </label>\n\n  <label *ngIf=\"!forFileUpload\"\n    class=\"col-form-label\"\n    [class.radio-label]=\"forRadioButtons\"\n    [class.radio-label-no-margin]=\"!!description\"\n    [for]=\"name\"\n    container=\"body\"\n    [ngStyle]=\"!!cursorStyle ? {\n      cursor: cursorStyle\n    } : null\"\n    [class.sr-only]=\"srOnly\">\n    <ng-template *ngTemplateOutlet=\"innerTooltip\"></ng-template>\n  </label>\n\n  <div *ngIf=\"description\"\n    [class.sr-only]=\"srOnly\"\n    class=\"label-desc\"\n    [innerHTML]=\"description\">\n  </div>\n</ng-container>\n\n\n<ng-template #innerTooltip>\n  <span\n    [innerHTML]=\"label\"\n    class=\"form-label\"\n    [class.asterisk-wrapper]=\"asteriskForRequired\">\n  </span>\n  <yc-fa *ngIf=\"(tooltipText || popoverTemplate)\"\n    icon=\"question-circle\"\n    [tooltip]=\"tooltipText\"\n    [popoverTemplate]=\"popoverTemplate\"\n    [adaptivePosition]=\"false\"\n    conatiner=\"body\"\n    [class.cursor-p]=\"!!popoverTemplate\"\n    class=\"ms-2 text-link\">\n  </yc-fa>\n\n  <yc-fa *ngIf=\"customIcon\"\n    [icon]=\"customIcon\"\n    [tooltip]=\"customIconTooltip\"\n    [adaptivePosition]=\"false\"\n    conatiner=\"body\"\n    class=\"ms-3\"\n    [ngClass]=\"customIconClass\">\n  </yc-fa>\n\n  <ng-content></ng-content>\n</ng-template>", styles: [".radio-label{font-size:1rem;font-weight:500;margin-bottom:1rem;align-items:center}.radio-label.radio-label-no-margin{margin-bottom:0}.form-label{position:relative}.form-label.asterisk-wrapper:after{position:absolute;color:#eb0000;content:\" *\"}.label-desc{font-size:12px;margin-bottom:1rem}\n"] }]
        }], propDecorators: { name: [{
                type: Input
            }], label: [{
                type: Input
            }], srOnly: [{
                type: Input
            }], description: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], cursorStyle: [{
                type: Input
            }], tooltipText: [{
                type: Input
            }], popoverTemplate: [{
                type: Input
            }], showQuestionMarkForTooltip: [{
                type: Input
            }], asteriskForRequired: [{
                type: Input
            }], forRadioButtons: [{
                type: Input
            }], customIcon: [{
                type: Input
            }], customIconTooltip: [{
                type: Input
            }], customIconClass: [{
                type: Input
            }], forFileUpload: [{
                type: Input
            }], triggerUpload: [{
                type: Input
            }], fileSelect: [{
                type: ViewChild,
                args: ['fileSelect']
            }] } });

class FormComponent {
    constructor() {
        this.onSubmit = new EventEmitter();
        this.submitting$ = new BehaviorSubject(false);
    }
    _submit() {
        if (this.submitDisabled) {
            return;
        }
        this.onSubmit.emit();
        this.submitting$.next(true);
        let inputObservable = this.formSubmit?.(this.formGroup);
        if (inputObservable instanceof Promise) {
            inputObservable = from(inputObservable);
        }
        else if (!(inputObservable instanceof Observable)) {
            inputObservable = timer(3000);
        }
        inputObservable
            .pipe(catchError((e) => {
            this.submitting$.next(false);
            return throwError(() => e);
        }))
            .subscribe(() => {
            this.submitting$.next(false);
        });
    }
    _handleSubmit(event) {
        event.preventDefault();
        this._submit();
    }
    ngOnDestroy() {
        this.submitting$.complete();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormComponent, isStandalone: false, selector: "yc-form", inputs: { formGroup: "formGroup", formSubmit: "formSubmit", submitDisabled: "submitDisabled" }, outputs: { onSubmit: "onSubmit" }, ngImport: i0, template: "<form (submit)=\"_handleSubmit($event)\">\n  <ng-content></ng-content>\n</form>", styles: ["form{display:flex;flex-direction:column}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-form', standalone: false, template: "<form (submit)=\"_handleSubmit($event)\">\n  <ng-content></ng-content>\n</form>", styles: ["form{display:flex;flex-direction:column}\n"] }]
        }], propDecorators: { formGroup: [{
                type: Input
            }], formSubmit: [{
                type: Input
            }], submitDisabled: [{
                type: Input
            }], onSubmit: [{
                type: Output
            }] } });

class FormSubmitComponent {
    constructor(formComponent) {
        this.formComponent = formComponent;
        this.disabled = false;
        this.primary = true;
        this.preventClickTracking = false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormSubmitComponent, deps: [{ token: FormComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormSubmitComponent, isStandalone: false, selector: "yc-submit", inputs: { disabled: "disabled", primary: "primary", preventClickTracking: "preventClickTracking", customColor: "customColor", buttonElementId: "buttonElementId" }, ngImport: i0, template: "<yc-button\n  [primary]=\"primary\"\n  [preventClickTracking]=\"preventClickTracking\"\n  buttonType=\"submit\"\n  [disabled]=\"disabled ||\n    (formComponent.submitting$ | async) ||\n    !formComponent.formGroup.valid\"\n  [customColor]=\"customColor ? customColor : undefined\"\n  [buttonElementId]=\"buttonElementId\">\n  <ng-content></ng-content>\n</yc-button>\n", styles: [""], dependencies: [{ kind: "component", type: i2$1.ButtonComponent, selector: "yc-button", inputs: ["tabIndex", "disabled", "size", "fontSize", "buttonType", "primary", "link", "linkPath", "icon", "secondary", "textDanger", "textWarning", "textPrimary", "textSuccess", "borderOnHover", "noClick", "badgeValue", "value", "ariaDescribedBy", "minWidth", "alignTop", "tooltipText", "popoverTemplate", "container", "popoverContext", "popoverIsOpen", "popoverOutsideClickToClose", "placement", "adaptivePosition", "langKey", "defaultText", "translatedText", "fontAwesomeIcon", "isDropdownButton", "isFooterButton", "dropdownDirection", "dropdownActions", "isDropdownToggle", "isSplitDropdownToggle", "preventClickTracking", "leftAlignLinkText", "analyticsEventName", "customColor", "buttonElementId", "includeExpandedAria", "ariaExpanded"], outputs: ["onClick", "popoverIsOpenChange", "onPopoverReady", "onPopoverClick"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormSubmitComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-submit', standalone: false, template: "<yc-button\n  [primary]=\"primary\"\n  [preventClickTracking]=\"preventClickTracking\"\n  buttonType=\"submit\"\n  [disabled]=\"disabled ||\n    (formComponent.submitting$ | async) ||\n    !formComponent.formGroup.valid\"\n  [customColor]=\"customColor ? customColor : undefined\"\n  [buttonElementId]=\"buttonElementId\">\n  <ng-content></ng-content>\n</yc-button>\n" }]
        }], ctorParameters: () => [{ type: FormComponent }], propDecorators: { disabled: [{
                type: Input
            }], primary: [{
                type: Input
            }], preventClickTracking: [{
                type: Input
            }], customColor: [{
                type: Input
            }], buttonElementId: [{
                type: Input
            }] } });

class CoreFormsModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CoreFormsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CoreFormsModule, declarations: [FormComponent,
            FormControlComponent,
            FormErrorComponent,
            FormHelpComponent,
            FormLabelComponent,
            FormSubmitComponent,
            FormHelpOrErrorComponent], imports: [I18nModule,
            IconsModule, i2.TooltipModule, CommonModule,
            ButtonModule,
            UtilsModule], exports: [FormComponent,
            FormControlComponent,
            FormErrorComponent,
            FormHelpComponent,
            FormLabelComponent,
            FormSubmitComponent,
            FormHelpOrErrorComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CoreFormsModule, imports: [I18nModule,
            IconsModule,
            TooltipModule.forRoot(),
            CommonModule,
            ButtonModule,
            UtilsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CoreFormsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        I18nModule,
                        IconsModule,
                        TooltipModule.forRoot(),
                        CommonModule,
                        ButtonModule,
                        UtilsModule
                    ],
                    declarations: [
                        FormComponent,
                        FormControlComponent,
                        FormErrorComponent,
                        FormHelpComponent,
                        FormLabelComponent,
                        FormSubmitComponent,
                        FormHelpOrErrorComponent
                    ],
                    exports: [
                        FormComponent,
                        FormControlComponent,
                        FormErrorComponent,
                        FormHelpComponent,
                        FormLabelComponent,
                        FormSubmitComponent,
                        FormHelpOrErrorComponent
                    ]
                }]
        }] });

class TypeSafeFormBuilder {
    constructor(formBuilder) {
        this.formBuilder = formBuilder;
    }
    group(controls, config) {
        return this.formBuilder.group(controls, config);
    }
    array(groups, config) {
        return this.formBuilder.array(groups, config);
    }
    control(...args) {
        return this.formBuilder.control(...args);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TypeSafeFormBuilder, deps: [{ token: i1.UntypedFormBuilder }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TypeSafeFormBuilder, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TypeSafeFormBuilder, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.UntypedFormBuilder }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CoreFormsModule, FormComponent, FormControlComponent, FormControlService, FormErrorComponent, FormHelpComponent, FormHelpOrErrorComponent, FormLabelComponent, FormSubmitComponent, TypeSafeFormBuilder };
